/*
 * Copyright (c) 2023. Word Wide Generation Ltd
 */


import { SetupTaskAction, SetupTaskUserAction, TaskWithAction } from '../../../types/setup-task';
import classnames from 'classnames';
import LoadingButton from '@g17eco/molecules/button/LoadingButton';
import { MouseEventHandler } from 'react';

export type ActionTracker = Record<string, SetupTaskAction | undefined>

export interface TodoItemProps {
  item: TaskWithAction;
  onAction: (item: { _id: string }, action: SetupTaskAction | SetupTaskUserAction) => Promise<unknown>;
  actionInProgress?: SetupTaskAction;
}

const openActionUrl = (item: TaskWithAction) => {
  if (item.url) {
    window.open(item.url, '_self');
  }
};

export const TodoItem = ({ item, onAction, actionInProgress }: TodoItemProps) => {

  const isCompleted = Boolean(item.action?.completedDate);
  const hasAction = Boolean(actionInProgress);


  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    // ignore event when user clicks inside the menu
    if (event.isDefaultPrevented()) {
      return;
    }
    if (item.url) {
      // We wait for the onAction before navigating to the new url, to prevent race conditions
      Promise.race([
        onAction(item, SetupTaskAction.Complete),
        new Promise((resolve) => setTimeout(resolve, 1_000))
      ]).then(() => openActionUrl(item));
    }
  };


  return (
    <div className={'d-flex align-items-center py-2'}>
      <div className={'flex-shrink align-self-start text-center'} style={{ minWidth: '2rem'}}>
        {isCompleted ?
          <i className='fa-solid fa-circle-check text-lg text-AccentMedium'/> :
          <LoadingButton
            disabled={hasAction && actionInProgress !== SetupTaskAction.Complete}
            onClick={() => onAction(item, SetupTaskAction.Complete)}
            block
            className={'btn-link p-0'}
            loading={actionInProgress === SetupTaskAction.Complete}>
            <i className='fa-light fa-circle text-ThemeTextMedium' />
          </LoadingButton>
        }
      </div>
      <div onClick={handleClick} className={classnames('w-100 flex-grow mx-1', {
        'text-decoration-line-through': isCompleted,
        'cursor-pointer': item.url,
      })}>
        <div>{item.name}</div>
        <article className={'text-sm text-ThemeTextMedium'}>{item.description}</article>
      </div>
      <div className={'d-flex flex-shrink align-self-start'}>
        <LoadingButton
          onClick={() => onAction(item, SetupTaskUserAction.ConfirmDelete)}
          loading={actionInProgress === SetupTaskAction.Delete}
          disabled={hasAction && actionInProgress !== SetupTaskAction.Delete}
          className={'btn-link  p-0'}
          block
        >
          <i className={'fa-light fa-trash-can text-ThemeTextMedium text-md'}/>
        </LoadingButton>
      </div>
    </div>
  )
}
